/**
 * TemplateRenderer — Loads a template definition and renders it onto the Fabric.js canvas.
 *
 * Coordinates background processing and zone creation.
 * Zones are passed through at their original template coordinates (1:1 with canvas).
 */
import * as fabric from 'fabric';
import { ZoneFactory } from './ZoneFactory.js';
import { BackgroundProcessor } from '../background/BackgroundProcessor.js';

export class TemplateRenderer {
  /**
   * @param {import('./CanvasManager.js').CanvasManager} canvasManager
   */
  constructor(canvasManager) {
    this.canvasManager = canvasManager;
    this.zoneFactory = new ZoneFactory();
    this.backgroundProcessor = new BackgroundProcessor();
    this._currentTemplate = null;
    this._currentVariables = {};
  }

  /**
   * Get the currently loaded template.
   * @returns {object|null}
   */
  getCurrentTemplate() {
    return this._currentTemplate;
  }

  /**
   * Render a template with the given variables.
   * Uses a version counter so the latest render call always wins —
   * if a new render starts while one is in progress, the old one
   * bails out at its next async step.
   * @param {object} template - Template JSON definition
   * @param {object} variables - Variable bindings (text, images, etc.)
   * @returns {Promise<void>}
   */
  async render(template, variables = {}) {
    this._renderVersion = (this._renderVersion || 0) + 1;
    const thisVersion = this._renderVersion;

    const previousTemplate = this._currentTemplate;
    this._currentTemplate = template;
    this._currentVariables = variables;

    this._showRenderOverlay('Rendering…');

    // Track selected zone to restore after re-render
    const canvas = this.canvasManager.canvas;
    const selectedZoneId = canvas.getActiveObject()?.get('zoneId') || null;

    try {
      // Sync current Fabric object positions back into zone definitions so
      // user-dragged positions survive re-renders (e.g. when a 3D transform
      // slider triggers a full re-render).
      // Only do this when re-rendering the SAME template — when switching
      // templates, old positions belong to a different layout and must not
      // overwrite the new template's bounds.
      // Only sync x/y (drag position) — never width/height, because those
      // may have just been updated via the properties panel and the old
      // Fabric object's scale still reflects the pre-edit size.
      const sameTemplate = previousTemplate && previousTemplate.id === template.id;
      const oldObjects = sameTemplate ? canvas.getObjects() : [];
      for (const obj of oldObjects) {
        const zid = obj.get('zoneId');
        if (!zid) continue;
        const zone = template.zones.find(z => z.id === zid);
        if (!zone) continue;
        const zType = obj.get('zoneType');
        if (zType === 'magazineCover' || zType === 'magazineSpread') {
          // Magazine objects are centered within bounds by _placeRenderInBounds.
          // Reverse the centering offset to recover bounds.x/y.
          const zb = obj.get('zoneBounds');
          if (zb) {
            const placedSize = Math.max(zb.width, zb.height);
            zone.bounds.x = obj.left - (zb.width - placedSize) / 2;
            zone.bounds.y = obj.top - (zb.height - placedSize) / 2;
          }
        } else {
          zone.bounds.x = obj.left;
          zone.bounds.y = obj.top;
        }
      }

      // Set canvas size from template
      this.canvasManager.setSize(template.canvas.width, template.canvas.height);

      // Clear existing objects
      canvas.clear();

      // 1. Render background
      this._showRenderOverlay('Rendering background…');
      await this._renderBackground(template, variables);
      if (this._renderVersion !== thisVersion) return; // superseded

      // 2. Render each zone in order (z-order = array order)
      for (let i = 0; i < template.zones.length; i++) {
        const zone = template.zones[i];
        this._showRenderOverlay(`Rendering ${zone.label || zone.id || 'zone'}… (${i + 1}/${template.zones.length})`);
        try {
          const fabricObj = await this.zoneFactory.createZone(zone, variables);
          if (this._renderVersion !== thisVersion) return; // superseded
          if (fabricObj) {
            canvas.add(fabricObj);
          }
        } catch (err) {
          console.error(`Failed to render zone "${zone.id}":`, err);
        }
        // Yield to event loop so UI can update
        await new Promise(r => setTimeout(r, 0));
      }

      if (this._renderVersion !== thisVersion) return; // superseded
      canvas.renderAll();

      // Restore selection if a zone was previously selected
      if (selectedZoneId) {
        const obj = canvas.getObjects().find(o => o.get('zoneId') === selectedZoneId);
        if (obj) {
          canvas.setActiveObject(obj);
          canvas.renderAll();
        }
      }
    } catch (err) {
      console.error('Render failed:', err);
    } finally {
      if (this._renderVersion === thisVersion) {
        this._hideRenderOverlay();
      }
    }
  }

  /**
   * Re-render the current template with updated variables.
   * @param {object} [updatedVariables] - Partial variable updates (merged with existing)
   * @returns {Promise<void>}
   */
  async rerender(updatedVariables = {}) {
    if (!this._currentTemplate) return;

    // Merge updated variables
    this._currentVariables = { ...this._currentVariables, ...updatedVariables };
    await this.render(this._currentTemplate, this._currentVariables);
  }

  /**
   * Re-render a single zone in place, preserving z-order and selection.
   * Much cheaper than a full template render — used for live orbit/zoom drags
   * on magazine zones so only one 3D render runs per frame.
   * @param {string} zoneId
   * @returns {Promise<void>}
   */
  async rerenderZone(zoneId) {
    const template = this._currentTemplate;
    if (!template) return;
    const zone = template.zones.find(z => z.id === zoneId);
    if (!zone) return;

    const canvas = this.canvasManager.canvas;
    const objects = canvas.getObjects();
    const oldObj = objects.find(o => o.get('zoneId') === zoneId);
    const index = oldObj ? objects.indexOf(oldObj) : objects.length;
    const wasActive = canvas.getActiveObject() === oldObj;

    // Sync the old object's position back to zone.bounds so the re-created
    // object appears in the same spot the user dragged it to.
    // Only sync x/y — never width/height, because those may have just been
    // updated via the properties panel and the old Fabric object's scale
    // still reflects the pre-edit size.
    if (oldObj) {
      const zType = oldObj.get('zoneType');
      if (zType === 'magazineCover' || zType === 'magazineSpread') {
        const zb = oldObj.get('zoneBounds');
        if (zb) {
          const placedSize = Math.max(zb.width, zb.height);
          zone.bounds.x = oldObj.left - (zb.width - placedSize) / 2;
          zone.bounds.y = oldObj.top - (zb.height - placedSize) / 2;
        }
      } else {
        zone.bounds.x = oldObj.left;
        zone.bounds.y = oldObj.top;
      }
    }

    const newObj = await this.zoneFactory.createZone(zone, this._currentVariables || {});
    if (!newObj) return;

    if (oldObj) canvas.remove(oldObj);
    canvas.add(newObj);
    if (typeof canvas.moveObjectTo === 'function') {
      canvas.moveObjectTo(newObj, index);
    }
    if (wasActive) canvas.setActiveObject(newObj);
    canvas.renderAll();
  }

  /**
   * Update a single variable and re-render.
   * @param {string} key
   * @param {*} value
   */
  async updateVariable(key, value) {
    await this.rerender({ [key]: value });
  }

  /**
   * Show the render overlay with status text.
   * @param {string} text
   * @private
   */
  _showRenderOverlay(text) {
    const overlay = document.getElementById('render-overlay');
    const textEl = document.getElementById('render-overlay-text');
    if (overlay) overlay.style.display = 'flex';
    if (textEl) textEl.textContent = text;
  }

  /**
   * Hide the render overlay.
   * @private
   */
  _hideRenderOverlay() {
    const overlay = document.getElementById('render-overlay');
    if (overlay) overlay.style.display = 'none';
  }

  /**
   * Render the template background.
   * @private
   */
  async _renderBackground(template, variables) {
    const bgDef = template.background;
    const canvas = this.canvasManager.canvas;

    if (!bgDef) {
      canvas.backgroundColor = template.canvas.backgroundColor || '#1a1a2e';
      return;
    }

    if (bgDef.type === 'color') {
      canvas.backgroundColor = bgDef.color || '#1a1a2e';
      return;
    }

    if (bgDef.type === 'gradient') {
      canvas.backgroundColor = bgDef.color || '#1a1a2e';
      // Create gradient background as a rect
      const gradRect = new fabric.Rect({
        left: 0,
        top: 0,
        width: template.canvas.width,
        height: template.canvas.height,
        originX: 'left',
        originY: 'top',
        selectable: false,
        evented: false,
      });

      const gradientDef = bgDef.gradient || {};
      const gradient = new fabric.Gradient({
        type: gradientDef.type || 'linear',
        coords: gradientDef.coords || {
          x1: 0, y1: 0,
          x2: template.canvas.width, y2: template.canvas.height,
        },
        colorStops: gradientDef.colorStops || [
          { offset: 0, color: gradientDef.startColor || '#d84627' },
          { offset: 1, color: gradientDef.endColor || '#d8583c' },
        ],
      });

      gradRect.set('fill', gradient);
      canvas.add(gradRect);
      return;
    }

    if (bgDef.type === 'image') {
      const bgImage = variables.backgroundImage || variables.coverImage;

      if (!bgImage) {
        // No background image provided — use solid fallback
        canvas.backgroundColor = template.canvas.backgroundColor || '#1a1a2e';
        return;
      }

      try {
        const processing = bgDef.processing || {};
        const processedCanvas = this.backgroundProcessor.process(bgImage, {
          canvasWidth: template.canvas.width,
          canvasHeight: template.canvas.height,
          blur: processing.blur ?? 15,
          brightness: processing.brightness ?? -0.3,
          overlay: processing.overlay || 'rgba(0,0,0,0.3)',
          cropRegion: variables._backgroundCropRegion || bgDef.cropRegion || null,
        });

        const bgFabricImg = new fabric.FabricImage(processedCanvas, {
          left: 0,
          top: 0,
          originX: 'left',
          originY: 'top',
          selectable: false,
          evented: false,
          excludeFromExport: false,
        });

        canvas.add(bgFabricImg);
      } catch (err) {
        console.error('Failed to process background image:', err);
        canvas.backgroundColor = template.canvas.backgroundColor || '#1a1a2e';
      }
    }
  }
}
